// components/monitoring/SpeedFusionLiveStatus.jsx
//
// Self-contained, real-time SpeedFusion VPN status panel — same pattern as
// RealtimeBandwidthChart: give it a `serialNumber`, it polls on its own timer
// and renders nothing until it actually has SpeedFusion peer data.
//
// NOTE ON ENDPOINTS (please confirm with backend):
// RealtimeBandwidthChart calls a same-origin, backend-owned route
// (`/api/peplink/bandwidth?serial_number=...`) — the backend resolves
// organization_id / network_id / device_id from the serial number itself and
// proxies to Peplink. The frontend never sees those ids (MainPage.js only
// ever has `serialNumber` and an internal `selectedDeviceId`), so this
// component follows the exact same convention: it calls two new backend
// routes below, using POST, passing only `serial_number`. Swap the two URL
// constants if your backend team names the routes differently.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// ─── Endpoints (same convention/origin as RealtimeBandwidthChart's "/api/peplink/*") ──
const STATUS_URL = '/api/peplink/speedfusion-status';       // -> proxies pepvpn/status_v2
const TUNNEL_STAT_URL = '/api/peplink/speedfusion-tunnel-stat'; // -> proxies pepvpn/tunnel_stat_v2

const POLL_INTERVAL_MS = 5000;   // how often we re-poll both endpoints
const MAX_HISTORY = 40;          // points kept per WAN for the mini charts

const CHART_WIDTH = 380;
const CHART_HEIGHT = 160;
const Y_AXIS_WIDTH = 44;
const X_AXIS_HEIGHT = 22;
const LATENCY_COLOR = '#CAFB6D';

// ─── Formatting helpers ───────────────────────────────────────────────────
const fmtKbps = (kbps) => {
  if (kbps == null || isNaN(kbps)) return '-';
  const v = Math.max(0, kbps);
  return v >= 1000 ? `${(v / 1000).toFixed(1)} Mbps` : `${Math.round(v)} kbps`;
};
const fmtLatency = (ms) => (ms == null || isNaN(ms) ? '-' : `${Math.round(ms)} ms`);
const fmtDropRate = (perSec) =>
  perSec == null || isNaN(perSec) ? '-' : `${perSec < 1 ? perSec.toFixed(2) : Math.round(perSec)}/s`;

const dotColor = (state) => {
  if (state === 'ACTIVE') return '#88F4EB';
  if (state === 'STANDBY') return '#CAFB6D';
  return '#D7D7CC'; // WAN_DOWN, DISABLED, missing
};

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST', // per spec: POST instead of GET
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  return res.json();
}

// ─── Main component ───────────────────────────────────────────────────────
const SpeedFusionLiveStatus = ({ serialNumber }) => {
  // peers: [{ peerId, name, client, routes, deviceStatus, remoteDeviceStatus }]
  const [peers, setPeers] = useState([]);
  // tunnels: { [peerId]: { [wanId]: { name, state, rtt, rxKbps, txKbps, dropPerSec, hasStats } } }
  const [tunnels, setTunnels] = useState({});
  // history: { [peerId]: { [wanId]: [{ t, rx, tx, latency }] } }
  const [history, setHistory] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [hasFetched, setHasFetched] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // ── Responsive layout: everything below reacts to this instead of hard
  // -coding desktop's fixed grid columns / chart width onto a narrow screen.
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = viewportWidth < 768;
  // Shrink the mini charts to fit the narrower screen instead of forcing the
  // desktop's fixed 380px width (which is what was causing the overflow/mess).
  const chartWidth = isMobile ? Math.max(240, Math.min(CHART_WIDTH, viewportWidth - 64)) : CHART_WIDTH;

  // Keeps last cumulative rx/tx/loss per peer+WAN so we can turn Peplink's
  // running totals into a live kbps / drops-per-second rate between polls.
  const prevRef = useRef({});
  const intervalRef = useRef(null);
  const isMounted = useRef(true);

  const fetchAll = useCallback(async () => {
    if (!serialNumber || !isMounted.current) return;
    try {
      const [statusData, tunnelData] = await Promise.all([
        postJSON(STATUS_URL, { serial_number: serialNumber }),
        postJSON(TUNNEL_STAT_URL, { serial_number: serialNumber }),
      ]);

      const peerList = statusData?.data?.peer_detail_list || [];
      const nextPeers = peerList.map((p) => ({
        peerId: p.peer_id,
        name: p.remote_name || p.username || p.peer_id,
        client: p.client || null,
        routes: p.route || [],
        deviceStatus: p.device_status,
        remoteDeviceStatus: p.remote_device_status,
      }));

      const now = Date.now();
      const tunnelStatList = (tunnelData?.data && tunnelData.data[0]?.tunnel_stat_list) || {};

      const nextTunnels = {};
      let nextHistory;
      setHistory((prevHistory) => {
        nextHistory = { ...prevHistory };
        return prevHistory; // real update happens after the loop below
      });
      nextHistory = nextHistory || {};

      nextPeers.forEach((peer) => {
        const wanMap = tunnelStatList[peer.peerId] || {};
        nextTunnels[peer.peerId] = {};
        const prevPeer = prevRef.current[peer.peerId] || {};
        const nextPrevPeer = {};

        Object.entries(wanMap).forEach(([wanId, w]) => {
          const hasStats = Array.isArray(w.rx) && Array.isArray(w.tx);
          const rxCum = hasStats ? w.rx[2] : null;
          const txCum = hasStats ? w.tx[2] : null;
          const lossCum = Array.isArray(w.loss) ? w.loss[2] : null;

          const prev = prevPeer[wanId];
          let rxKbps = null;
          let txKbps = null;
          let dropPerSec = null;

          if (prev && rxCum != null && txCum != null) {
            const dtSec = Math.max(0.001, (now - prev.time) / 1000);
            const dRx = rxCum - prev.rxCum;
            const dTx = txCum - prev.txCum;
            // Guard against counter resets (e.g. tunnel reconnect) going negative.
            rxKbps = dRx >= 0 ? (dRx * 8) / 1000 / dtSec : 0;
            txKbps = dTx >= 0 ? (dTx * 8) / 1000 / dtSec : 0;
            if (lossCum != null && prev.lossCum != null) {
              const dLoss = lossCum - prev.lossCum;
              dropPerSec = dLoss >= 0 ? dLoss / dtSec : 0;
            }
          }

          nextPrevPeer[wanId] = { rxCum, txCum, lossCum, time: now };

          nextTunnels[peer.peerId][wanId] = {
            name: w.name,
            state: w.state,
            rtt: typeof w.rtt === 'number' ? w.rtt : null,
            rxKbps,
            txKbps,
            dropPerSec,
            hasStats,
          };

          if (hasStats && rxKbps != null) {
            nextHistory[peer.peerId] = nextHistory[peer.peerId] || {};
            const arr = nextHistory[peer.peerId][wanId] || [];
            const point = { t: now, rx: rxKbps, tx: txKbps, latency: w.rtt || 0, drop: dropPerSec || 0 };
            const updated = [...arr, point];
            nextHistory[peer.peerId][wanId] = updated.length > MAX_HISTORY ? updated.slice(-MAX_HISTORY) : updated;
          }
        });

        prevRef.current[peer.peerId] = nextPrevPeer;
      });

      if (!isMounted.current) return;
      setPeers(nextPeers);
      setTunnels(nextTunnels);
      setHistory({ ...nextHistory });
      setLastUpdate(new Date());
      setHasFetched(true);
      setExpanded((prevExp) => {
        if (prevExp.size > 0 || nextPeers.length === 0) return prevExp;
        // Auto-expand the first peer once data first arrives (matches the
        // reference screenshot, which opens the one peer it has).
        return new Set([nextPeers[0].peerId]);
      });
    } catch (err) {
      console.error('Error fetching SpeedFusion status:', err);
      if (isMounted.current) setHasFetched(true); // stop showing a blank/loading state forever
    }
  }, [serialNumber]);

  useEffect(() => {
    if (!serialNumber) return;
    isMounted.current = true;
    setHasFetched(false);
    setPeers([]);
    setTunnels({});
    setHistory({});
    prevRef.current = {};

    fetchAll();
    intervalRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => {
      isMounted.current = false;
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber]);

  const togglePeer = (peerId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(peerId) ? next.delete(peerId) : next.add(peerId);
      return next;
    });
  };

  // ── Hide the whole panel until we know there's actually SpeedFusion data ──
  if (!hasFetched || peers.length === 0) return null;

  return (
    <div className="t247-panel" style={{ marginTop: 20 }}>
      <div className="t247-panel-header-row">
        <span>SPEEDFUSION VPN LIVE STATUS</span>
        <span className="t247-secondary-label">
          {lastUpdate ? `UPDATED ${lastUpdate.toLocaleTimeString()}` : 'LIVE'}
        </span>
      </div>

      {!isMobile && (
        <div className="t247-wan-headrow" style={{ gridTemplateColumns: '1.6fr 0.8fr 2fr' }}>
          <div>REMOTE PEER</div>
          <div>WAN</div>
          <div>IP / SUBNET ADDRESS(ES)</div>
        </div>
      )}

      {peers.map((peer) => {
        const wanEntries = Object.entries(tunnels[peer.peerId] || {}).sort(
          (a, b) => Number(a[0]) - Number(b[0])
        );
        const activeWanIds = wanEntries
          .filter(([, w]) => w.state === 'ACTIVE')
          .map(([wanId]) => wanId);
        const isOpen = expanded.has(peer.peerId);
        const addressLabel = [
          peer.client ? `Client: ${peer.client}` : null,
          ...(peer.routes || []),
        ]
          .filter(Boolean)
          .join(', ');

        return (
          <div key={peer.peerId}>
            <div
              className="t247-wan-row"
              style={
                isMobile
                  ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, gridTemplateColumns: 'none' }
                  : { gridTemplateColumns: '1.6fr 0.8fr 2fr' }
              }
              onClick={() => togglePeer(peer.peerId)}
              role="button"
              tabIndex={0}
            >
              <div className="t247-wan-name">
                {isOpen ? '▼' : '▶'} {peer.name}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 400, fontFamily: 'var(--font-mono)' }}>
                {isMobile ? `WAN: ${activeWanIds.join(', ') || '-'}` : activeWanIds.join(', ') || '-'}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 400, fontFamily: 'var(--font-mono)', wordBreak: 'break-word' }}>
                {addressLabel || '-'}
              </div>
            </div>

            {isOpen && (
              <div style={{ padding: '4px 14px 18px' }}>
                {/* Per-WAN live row */}
                {wanEntries.map(([wanId, w]) =>
                  isMobile ? (
                    <div
                      key={wanId}
                      className="t247-wan-row"
                      style={{ display: 'flex', flexDirection: 'column', gap: 8, cursor: 'default', padding: '10px 14px' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 9,
                            height: 9,
                            flexShrink: 0,
                            background: dotColor(w.state),
                          }}
                        />
                        <span className="t247-wan-name" style={{ fontSize: 12 }}>
                          {w.name || `WAN ${wanId}`}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                        <div className="t247-num">Rx: {w.hasStats ? fmtKbps(w.rxKbps) : '-'}</div>
                        <div className="t247-num">Tx: {w.hasStats ? fmtKbps(w.txKbps) : '-'}</div>
                        <div className="t247-num">Drop: {w.hasStats ? fmtDropRate(w.dropPerSec) : '-'}</div>
                        <div className="t247-num">Lat: {w.hasStats ? fmtLatency(w.rtt) : '-'}</div>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={wanId}
                      className="t247-wan-row"
                      style={{ gridTemplateColumns: '18px 1.1fr 1fr 1fr 1fr 1fr', cursor: 'default' }}
                    >
                      <div>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 9,
                            height: 9,
                            background: dotColor(w.state),
                          }}
                        />
                      </div>
                      <div className="t247-wan-name" style={{ fontSize: 12 }}>
                        {w.name || `WAN ${wanId}`}
                      </div>
                      <div className="t247-num">Rx: {w.hasStats ? fmtKbps(w.rxKbps) : '-'}</div>
                      <div className="t247-num">Tx: {w.hasStats ? fmtKbps(w.txKbps) : '-'}</div>
                      <div className="t247-num">Drop: {w.hasStats ? fmtDropRate(w.dropPerSec) : '-'}</div>
                      <div className="t247-num">Lat: {w.hasStats ? fmtLatency(w.rtt) : '-'}</div>
                    </div>
                  )
                )}

                {/* Per-WAN mini throughput + latency chart */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    flexWrap: 'wrap',
                    gap: isMobile ? 12 : 16,
                    marginTop: 12,
                  }}
                >
                  {wanEntries.map(([wanId, w]) => (
                    <WanMiniChart
                      key={wanId}
                      title={w.name || `WAN ${wanId}`}
                      points={history[peer.peerId]?.[wanId] || []}
                      width={chartWidth}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Small dual-axis (throughput kbps + latency ms) sparkline per WAN ─────
// x-axis here = each poll (one point roughly every POLL_INTERVAL_MS, oldest
// on the left, newest on the right) — see chat explanation for more detail.
// Desktop: hover to see the value at that point. Mobile (no real hover): tap
// a point to pin the tooltip there, tap again (or tap elsewhere) to move it.
const WanMiniChart = ({ title, points, width = CHART_WIDTH }) => {
  const hasData = points.length >= 2;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const svgRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const maxThroughput = useMemo(() => {
    if (!hasData) return 1000;
    const max = Math.max(...points.map((p) => Math.max(p.rx, p.tx)));
    return Math.max(100, Math.ceil(max * 1.2));
  }, [points, hasData]);

  const maxLatency = useMemo(() => {
    if (!hasData) return 100;
    const max = Math.max(...points.map((p) => p.latency || 0));
    return Math.max(20, Math.ceil(max * 1.2));
  }, [points, hasData]);

  const getX = (i) =>
    Y_AXIS_WIDTH + (i / Math.max(1, points.length - 1)) * (width - Y_AXIS_WIDTH - 10);
  const getYThroughput = (v) =>
    CHART_HEIGHT - X_AXIS_HEIGHT - (v / maxThroughput) * (CHART_HEIGHT - X_AXIS_HEIGHT - 14);
  const getYLatency = (v) =>
    CHART_HEIGHT - X_AXIS_HEIGHT - (v / maxLatency) * (CHART_HEIGHT - X_AXIS_HEIGHT - 14);

  // Smooth cubic-bezier path (control points share the midpoint x, so the
  // curve stays flat at each sample and just rounds the transitions between
  // them) — same technique RealtimeBandwidthChart uses for its lines.
  const path = (getY, key) => {
    if (!hasData) return '';
    const pts = points.map((p, i) => ({ x: getX(i), y: getY(p[key]) }));
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      const cpx = (prev.x + curr.x) / 2;
      d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
    }
    return d;
  };

  const findClosestIndex = (clientX) => {
    if (!svgRef.current || !hasData) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = clientX - rect.left;
    let closest = -1;
    let minDist = Infinity;
    points.forEach((_, i) => {
      const dist = Math.abs(mouseX - getX(i));
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    return closest;
  };

  const handleMouseMove = (e) => {
    if (isMobile) return; // mobile uses tap, not hover
    const idx = findClosestIndex(e.clientX);
    if (idx != null) setActiveIndex(idx);
  };
  const handleMouseLeave = () => {
    if (isMobile) return;
    setActiveIndex(null);
  };
  const handleClick = (e) => {
    if (!isMobile) return; // desktop already shows it on hover
    const idx = findClosestIndex(e.clientX);
    setActiveIndex((prev) => (prev === idx ? null : idx));
  };

  const active = activeIndex != null ? points[activeIndex] : null;
  // Keep the tooltip on-screen near the right edge of the chart.
  const tooltipLeft = active
    ? Math.min(getX(activeIndex) + 8, width - 108)
    : 0;

  return (
    <div
      style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border, rgba(255,255,255,0.12))',
        padding: 8,
        minWidth: width,
        width,
        position: 'relative',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontFamily: "'DM Mono', monospace",
          color: 'var(--color-label)',
          marginBottom: 4,
        }}
      >
        {title.toUpperCase()}
      </div>
      <svg
        ref={svgRef}
        width={width}
        height={CHART_HEIGHT}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: hasData ? 'crosshair' : 'default' }}
      >
        {hasData ? (
          <>
            <path d={path(getYThroughput, 'tx')} stroke="#88F4EB" strokeWidth="1.5" fill="none" />
            <path d={path(getYThroughput, 'rx')} stroke="#D7D7CC" strokeWidth="1.5" fill="none" />
            <path
              d={path(getYLatency, 'latency')}
              stroke={LATENCY_COLOR}
              strokeWidth="1.5"
              fill="none"
              strokeDasharray="3,2"
            />
            {/* Packet-drop markers: a small triangle on the latency line at
                any sample where drops occurred. */}
            {points.map((p, i) =>
              p.drop > 0 ? (
                <polygon
                  key={`drop-${i}`}
                  points={`${getX(i)},${getYLatency(p.latency) - 7} ${getX(i) - 4},${getYLatency(p.latency) - 1} ${getX(i) + 4},${getYLatency(p.latency) - 1}`}
                  fill={LATENCY_COLOR}
                />
              ) : null
            )}
            {active && (
              <>
                <line
                  x1={getX(activeIndex)}
                  y1={0}
                  x2={getX(activeIndex)}
                  y2={CHART_HEIGHT - X_AXIS_HEIGHT}
                  stroke="rgba(255,255,255,0.25)"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
                <circle cx={getX(activeIndex)} cy={getYThroughput(active.tx)} r="3" fill="#88F4EB" />
                <circle cx={getX(activeIndex)} cy={getYThroughput(active.rx)} r="3" fill="#D7D7CC" />
                <circle cx={getX(activeIndex)} cy={getYLatency(active.latency)} r="3" fill={LATENCY_COLOR} />
              </>
            )}
          </>
        ) : (
          <text
            x={width / 2}
            y={CHART_HEIGHT / 2}
            textAnchor="middle"
            fill="var(--color-label)"
            fontSize="11"
          >
            Waiting for data...
          </text>
        )}
      </svg>

      {active && (
        <div
          style={{
            position: 'absolute',
            top: 26,
            left: tooltipLeft,
            background: 'var(--navy-800, #112023)',
            border: '1px solid rgba(136,244,235,0.3)',
            padding: '6px 9px',
            fontSize: 10.5,
            fontFamily: "'DM Mono', monospace",
            color: '#FFFFFF',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          <div style={{ marginBottom: 4, opacity: 0.7 }}>
            {new Date(active.t).toLocaleTimeString()}
          </div>
          <div style={{ color: '#88F4EB' }}>Tx: {fmtKbps(active.tx)}</div>
          <div style={{ color: '#D7D7CC' }}>Rx: {fmtKbps(active.rx)}</div>
          <div style={{ color: LATENCY_COLOR }}>Lat: {fmtLatency(active.latency)}</div>
          <div style={{ color: LATENCY_COLOR }}>Drop: {fmtDropRate(active.drop)}</div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          fontSize: 10,
          fontFamily: "'DM Mono', monospace",
          color: 'var(--color-label)',
        }}
      >
        <span><span style={{ color: '#88F4EB' }}>■</span> Tx</span>
        <span><span style={{ color: '#D7D7CC' }}>■</span> Rx</span>
        <span><span style={{ color: LATENCY_COLOR }}>■</span> Latency</span>
        <span><span style={{ color: LATENCY_COLOR }}>▲</span> Packet Drop</span>
      </div>
    </div>
  );
};

export default SpeedFusionLiveStatus;
